/*
  <age-verifier> — age gate modal (logic ported 1:1 from the Concept theme's
  age-verifier, rebuilt as a self-contained alife Web Component — no ModalElement
  / theme.* dependencies).

  Behaviour:
    - Shows once after `data-delay` seconds, only if the visitor has not already
      confirmed (cookie) — unless `data-test-mode="true"`, which always shows and
      never persists the cookie.
    - "Yes" → hide + drop a cookie that lasts `data-expiry` days.
    - "No"  → follow the decline link if one is set (else just re-affirms the gate).
    - Overlay fades in, the card scales/fades up, and the side image slides open
      (`show-image`) — same choreography as Concept, driven by the `active` /
      `show-image` classes and CSS transitions.
    - Theme editor: shows on section select, hides on deselect, never sets a cookie.
    - Body scroll is locked while open; focus is trapped; reduced motion respected.
*/
if (!customElements.get('age-verifier')) {
  customElements.define(
    'age-verifier',
    class AgeVerifier extends HTMLElement {
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay'), 10) : 1;
      }

      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry'), 10) : 30;
      }

      get cookieName() {
        return 'alife:age-verified';
      }

      connectedCallback() {
        this.overlay = this.querySelector('[data-overlay]');
        this.accept = this.querySelector('[data-accept]');
        this.decline = this.querySelector('[data-decline]');
        this.ask = this.querySelector('[data-ask]');
        this.denied = this.querySelector('[data-denied]');

        this.accept?.addEventListener('click', this.onAccept.bind(this));
        this.decline?.addEventListener('click', this.onDecline.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));

        if (Shopify.designMode) {
          const section = this.closest('.shopify-section');
          section?.addEventListener('shopify:section:select', () => this.show());
          section?.addEventListener('shopify:section:deselect', () => this.hide());
          return;
        }

        if (this.testMode || !this.getCookie(this.cookieName)) {
          this.timer = setTimeout(() => this.show(), this.delay * 1000);
        }
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        this.unlockScroll();
      }

      show() {
        if (this.hasAttribute('open')) return;

        this.hidden = false;
        this.setAttribute('open', '');
        this.lockScroll();

        // Next frame so the entrance transition runs from the hidden state.
        requestAnimationFrame(() => {
          this.setAttribute('active', '');
          this.classList.add('show-image');
        });

        // Defer focus until the card is interactive.
        setTimeout(() => this.accept?.focus(), 75);
      }

      hide() {
        if (!this.hasAttribute('open')) return;

        this.removeAttribute('active');
        this.classList.remove('show-image');
        this.unlockScroll();

        const done = () => {
          this.removeAttribute('open');
          this.hidden = true;
          this.overlay?.removeEventListener('transitionend', done);
        };

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || !this.overlay) {
          done();
        } else {
          this.overlay.addEventListener('transitionend', done, { once: true });
        }
      }

      onAccept(event) {
        event.preventDefault();

        // Test mode never persists — wipe any cookie a previous run left behind.
        if (this.testMode) {
          this.removeCookie(this.cookieName);
        } else {
          this.setCookie(this.cookieName, this.expiry);
        }

        this.hide();
      }

      onDecline(event) {
        event.preventDefault();

        // Visitor is under age — swap the question for the blocking denial
        // message. No cookie, no redirect: the page stays locked.
        this.ask?.setAttribute('hidden', '');
        if (this.denied) {
          this.denied.removeAttribute('hidden');
          this.denied.focus();
        }
      }

      onKeydown(event) {
        if (event.key !== 'Tab' || !this.hasAttribute('open')) return;

        const focusable = this.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      lockScroll() {
        document.body.classList.add('age-verifier-locked');
      }

      unlockScroll() {
        document.body.classList.remove('age-verifier-locked');
      }

      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${expiry * 24 * 60 * 60}; path=/`;
      }

      removeCookie(name) {
        document.cookie = `${name}=; max-age=0; path=/`;
      }
    }
  );
}
